import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Headphones, Loader2, Mail, Phone } from 'lucide-react'

import { useAuthConfig } from '@/hooks'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { resolveMediaDisplayUrl } from '@/shared/lib/media-proxy'

type Props = {
  open: boolean
  onClose: () => void
}

export function SupportContactDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const { config, reload } = useAuthConfig()
  const [refreshing, setRefreshing] = useState(false)

  // 打开时强制刷新：后台改客服配置后进程内 auth/config 缓存可能仍是旧值
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setRefreshing(true)
    void reload()
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setRefreshing(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, reload])

  const support = config?.support
  const phone = support?.phone?.trim() ?? ''
  const email = support?.email?.trim() ?? ''
  const message = support?.message?.trim() ?? ''
  const qrcodeUrl = useMemo(
    () => resolveMediaDisplayUrl(support?.qrcode_url),
    [support?.qrcode_url],
  )
  const hasContact = Boolean(phone || email || qrcodeUrl)

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md border-white/10 bg-[#14141d] text-zinc-100 sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-zinc-50">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-500/15 text-indigo-300">
              <Headphones size={18} />
            </span>
            {t('feedback.floatSupport')}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {message || t('feedback.supportDialog.subtitle')}
          </DialogDescription>
        </DialogHeader>

        {refreshing && !hasContact ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('feedback.supportDialog.loading')}
          </div>
        ) : hasContact ? (
          <div className="mt-2 space-y-3">
            {phone ? (
              <a
                href={`tel:${phone}`}
                className="flex items-start gap-3 rounded-xl border border-white/8 bg-black/25 px-4 py-3 transition hover:border-indigo-400/40 hover:bg-indigo-500/10"
              >
                <Phone size={16} className="mt-0.5 shrink-0 text-indigo-300" />
                <div className="min-w-0">
                  <div className="text-xs text-zinc-500">{t('feedback.supportDialog.phone')}</div>
                  <div className="mt-0.5 font-medium text-zinc-100">{phone}</div>
                </div>
              </a>
            ) : null}

            {email ? (
              <a
                href={`mailto:${email}`}
                className="flex items-start gap-3 rounded-xl border border-white/8 bg-black/25 px-4 py-3 transition hover:border-indigo-400/40 hover:bg-indigo-500/10"
              >
                <Mail size={16} className="mt-0.5 shrink-0 text-indigo-300" />
                <div className="min-w-0">
                  <div className="text-xs text-zinc-500">{t('feedback.supportDialog.email')}</div>
                  <div className="mt-0.5 break-all font-medium text-zinc-100">{email}</div>
                </div>
              </a>
            ) : null}

            {qrcodeUrl ? (
              <div className="flex flex-col items-center rounded-xl border border-white/8 bg-black/25 px-4 py-5">
                <div className="mb-3 text-xs text-zinc-500">{t('feedback.supportDialog.qrcode')}</div>
                <img
                  src={qrcodeUrl}
                  alt={t('feedback.supportDialog.qrcode')}
                  className="h-40 w-40 rounded-lg border border-white/10 bg-white object-contain p-1.5"
                />
              </div>
            ) : null}
          </div>
        ) : (
          <p className="mt-2 rounded-xl border border-dashed border-white/10 bg-black/20 px-4 py-6 text-center text-sm text-zinc-500">
            {t('feedback.supportDialog.empty')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  )
}
